import React, { useRef } from 'react';
import { Layout, Button, Modal, Flex } from 'antd';
import { useReactToPrint } from 'react-to-print';

import { useLanguage } from 'hooks/Language/useLanguage';
const appLogo = 'https://ossix.blob.core.windows.net/icons/ossix.png';

interface TransactionData {
  status: string;
  from: string;
  to: string;
  operator: string;
  amount: string;
  fees: string;
  externalFees: string;
  exchangeFees: string;
  description: string;
  reference: string;
  date: string;
  transactionId: string;
  accountNumber: string;
  accountName: string;
}

interface GenerateBillProps {
  transactionData?: TransactionData;
  open: boolean;
  onHandleClosePrintPage?: () => void;
}

const GenerateBill: React.FC<GenerateBillProps> = ({
  transactionData,
  open,
  onHandleClosePrintPage,
}) => {
  const { translate } = useLanguage();
  const componentRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  return (
    <Modal
      width={640}
      open={open}
      onCancel={onHandleClosePrintPage}
      footer={
        <Flex justify="space-between">
          <Button
            size="large"
            danger
            ghost
            type="primary"
            onClick={onHandleClosePrintPage}
          >
            {translate('Cancel')}
          </Button>
          <Button
            size="large"
            type="primary"
            onClick={() => {
              handlePrint();
              onHandleClosePrintPage?.();
            }}
          >
            {translate('Print')}
          </Button>
        </Flex>
      }
    >
      <div
        style={{
          fontFamily: 'Arial, sans-serif',
          maxWidth: '600px',
          margin: '0 auto',
          backgroundColor: '#f4f4f4',
          padding: '20px',
        }}
      >
        <TransactionDetails
          ref={componentRef}
          transactionData={transactionData}
        />
      </div>
    </Modal>
  );
};

const TransactionDetails = React.forwardRef<
  HTMLDivElement,
  { transactionData?: TransactionData }
>(({ transactionData }, ref) => {
  const { translate } = useLanguage();

  const renderTransactionRow = (label: string, value: string) => (
    <tr style={{ borderBottom: '1px solid #e0e4e6' }}>
      <td
        style={{
          padding: '10px',
          color: '#6c757d',
          width: '50%',
        }}
      >
        {label}
      </td>
      <td
        style={{
          padding: '10px',
          textAlign: 'right',
          color: '#2c3e50',
          width: '50%',
        }}
      >
        {value}
      </td>
    </tr>
  );

  if (!transactionData) return null;

  return (
    <div
      ref={ref}
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow:
          '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <tr>
              <td style={{ width: '50%', textAlign: 'left' }}>
                <img
                  src={appLogo}
                  alt="OSSIX Logo"
                  style={{ maxWidth: '70px', height: 'auto' }}
                />
              </td>
              <td
                style={{
                  width: '50%',
                  textAlign: 'right',
                  color: '#2c3e50',
                  fontWeight: 'bold',
                }}
              >
                {new Date().toLocaleString('en-US', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                })}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Content */}
      <div style={{ padding: '20px' }}>
        <h2
          style={{
            textAlign: 'center',
            color: '#2c3e50',
            marginBottom: '20px',
          }}
        >
          {translate('Transaction Details')}
        </h2>

        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
          }}
        >
          <tbody>
            {renderTransactionRow('Status', transactionData.status)}
            {renderTransactionRow('From', transactionData.from)}
            {renderTransactionRow('To', transactionData.to)}
            {renderTransactionRow(
              'Operator',
              transactionData.operator,
            )}
            {renderTransactionRow('Amount', transactionData.amount)}
            {renderTransactionRow('Fees', transactionData.fees)}
            {renderTransactionRow(
              'External Fees',
              transactionData.externalFees,
            )}
            {renderTransactionRow(
              'Exchange Fees',
              transactionData.exchangeFees,
            )}
            {renderTransactionRow(
              'Description',
              transactionData.description,
            )}
            {renderTransactionRow(
              'Reference',
              transactionData.reference,
            )}
            {renderTransactionRow('Date', transactionData.date)}
            {renderTransactionRow(
              'Transaction ID',
              transactionData.transactionId,
            )}
            {renderTransactionRow(
              'Account Number',
              transactionData.accountNumber,
            )}
            {renderTransactionRow(
              'Account Name',
              transactionData.accountName,
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderBottomLeftRadius: '12px',
          borderBottomRightRadius: '12px',
          textAlign: 'center',
          fontSize: '12px',
          color: '#6c757d',
        }}
      >
        <p style={{ marginBottom: '15px' }}>
          {translate(`The transaction shown on your receipt is correct at the time
          of downloading.`)}
        </p>
        <p style={{ marginBottom: '15px' }}>
          {translate(
            'If you think something is incorrect, please contact us',
          )}
        </p>

        <div style={{ marginTop: '20px' }}>
          <a
            href="https://apps.apple.com/app/gmoney-money/id6450957624"
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'inline-block', margin: '0 10px' }}
          >
            <img
              src="https://ossix.blob.core.windows.net/icons/appstore.png"
              alt="App Store"
              style={{ width: '120px', height: 'auto' }}
            />
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=your.bundleId"
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'inline-block', margin: '0 10px' }}
          >
            <img
              src="https://ossix.blob.core.windows.net/icons/googleplay.png"
              alt="Google Play"
              style={{ width: '120px', height: 'auto' }}
            />
          </a>
        </div>
      </div>
    </div>
  );
});

export default GenerateBill;
